import type { PDFSessionFactory } from "@pdfking/renderer-api"
import type { PdfiumApi } from "@pdfking/renderer-wasm-pdfium"
import { CreatePdfiumFactory } from "@pdfking/renderer-wasm-pdfium"
import { CreateSingleton, LockManager, WithCleanup } from "@pdfking/tfunc"

import { CreatePDFReader } from "../interface/CreatePDFReader"
import { CreatePDFWriter } from "../interface/CreatePDFWriter"

const INIT_TIMEOUT_SECONDS = 1
const DISPOSE_TIMEOUT_SECONDS = 5

let currentApi: PdfiumApi | undefined
let initTimer: ReturnType<typeof setTimeout> | undefined
let disposeTimer: ReturnType<typeof setTimeout> | undefined
let activeSessions = 0

const { schedule: lockSchedule } = LockManager()

/** Clears init and/or dispose timers to prevent stale callbacks. */
const clearTimers = (onInit = true, onDispose = true) => {
  if (onInit && initTimer) {
    clearTimeout(initTimer)
    initTimer = undefined
  }
  if (onDispose && disposeTimer) {
    clearTimeout(disposeTimer)
    disposeTimer = undefined
  }
}

/** Schedules a function under lock, then clears specified timers. */
const schedule = (
  func: () => Promise<void>,
  onInit: boolean = true,
  onDispose: boolean = true
) => lockSchedule("λ", func).then(() => clearTimers(onInit, onDispose))

/**
 * Schedules a session with automatic WASM memory management.
 *
 * After each session completes, two cleanup timers are scheduled:
 * - Init timer (1s): Resets WASM memory to free allocations while keeping module loaded
 * - Dispose timer (5s): Fully disposes the WASM module
 *
 * Timers are cancelled when a new session starts or when another timer fires.
 * Timers are not scheduled while sessions are active to prevent corruption.
 */
const sessionScheduler = <T>(
  init: () => Promise<void>,
  fn: () => Promise<T>,
  onInitTimeout: () => Promise<void>,
  onDisposeTimeout: () => Promise<void>
): Promise<T> =>
  schedule(async () => {
    activeSessions++
    await init()
  }).then(() =>
    WithCleanup(fn(), () => {
      activeSessions--
      clearTimers()
      if (activeSessions === 0) {
        initTimer = setTimeout(
          () => schedule(onInitTimeout, true, false),
          INIT_TIMEOUT_SECONDS * 1000
        )
        disposeTimer = setTimeout(
          () => schedule(onDisposeTimeout, false, true),
          DISPOSE_TIMEOUT_SECONDS * 1000
        )
      }
    })
  )

const loader = CreateSingleton(CreatePdfiumFactory)

/**
 * Creates a PDF session with managed WASM lifecycle.
 *
 * Provides reader/writer access to PDFium. Multiple concurrent sessions share
 * the same WASM instance. Memory is automatically cleaned up after inactivity:
 * reset the heap after 1s, fully disposed after 5s.
 */
export const CreatePDFSession: PDFSessionFactory = func =>
  loader().then(factory =>
    sessionScheduler(
      // Initialize API if needed (runs under lock)
      async () => {
        if (!currentApi) {
          currentApi = await factory()
          console.info("PDFSession: create", currentApi.id)
        }
      },
      // Execute user function (runs without lock)
      async () =>
        func({
          reader: opts => CreatePDFReader(currentApi!, opts),
          writer: () => CreatePDFWriter(currentApi!),
        }),
      // Reset WASM memory after 1s inactivity (runs under lock)
      async () => {
        console.info("PDFSession: init", currentApi?.id)
        await currentApi?.init()
      },
      // Dispose API after 5s inactivity (runs under lock)
      async () => {
        console.info("PDFSession: dispose", currentApi?.id)
        currentApi?.dispose()
        currentApi = undefined
      }
    )
  )
